<template>
  <div v-if="isReadyToShow">
    <p :class="$style.errorMessage">
      {{ parsedErrorMessage }}
      <a
        v-if="hasPrimaryLink && !hasSecondaryLink"
        :href="primaryHelp.url"
        :class="$style.helpLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ primaryHelp.text }}
        <ExternalLinkIcon :class="$style.externalLinkIcon" />
      </a>
    </p>
    <div
      v-if="hasPrimaryLink && hasSecondaryLink"
      :class="$style.linksContainer"
    >
      <a
        :href="primaryHelp.url"
        :class="$style.helpLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ primaryHelp.text }}
        <ExternalLinkIcon :class="$style.externalLinkIcon" />
      </a>
      <a
        :href="secondaryHelp.url"
        :class="$style.helpLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ secondaryHelp.text }}
        <ExternalLinkIcon :class="$style.externalLinkIcon" />
      </a>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import get from 'lodash/get';

import ExternalLinkIcon from 'public/img/icons/icon-external-link.svg?inline';
import Util from 'client/util/Util';
import api from 'client/api/UserLoginApi';

export default defineComponent({
  name: 'CustomError',

  components: {
    ExternalLinkIcon,
  },

  props: {
    errorMessage: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fetchedError: null,
      isResolvingParameterizedError: false,
    };
  },

  computed: {
    isReadyToShow() {
      return !this.isResolvingParameterizedError;
    },
    parsedError() {
      if (this.fetchedError) {
        return this.fetchedError;
      }
      // Attempt to parse the error message as JSON
      return Util.tryParseJson(this.errorMessage);
    },
    parsedErrorMessage() {
      return get(this.parsedError, 'message', '');
    },
    primaryHelp() {
      return get(this.parsedError, 'primaryHelp', null);
    },
    secondaryHelp() {
      return get(this.parsedError, 'secondaryHelp', null);
    },
    hasPrimaryLink() {
      return this.isValidHelpObject(this.primaryHelp);
    },
    hasSecondaryLink() {
      return this.isValidHelpObject(this.secondaryHelp);
    },
  },
  watch: {
    errorMessage: {
      immediate: true,
      async handler(newVal) {
        const details = typeof newVal === 'string'
          ? Util.parseParameterizedErrorDetails(newVal)
          : null;

        if (details) {
          this.fetchedError = null;
          this.isResolvingParameterizedError = true;
          try {
            const data = await api.fetchErrorDetail(details.type, details.code);
            this.fetchedError = data;
          } catch {
            this.fetchedError = { message: 'Access denied' };
          } finally {
            this.isResolvingParameterizedError = false;
            this.$emit('resolved');
          }
          return;
        }

        this.fetchedError = null;
        this.isResolvingParameterizedError = false;
        this.$emit('resolved');
      },
    },
  },
  methods: {
    isValidHelpObject(helpObject) {
      if (!helpObject || !helpObject.url || !helpObject.text) return false;
      try {
        const url = new URL(helpObject.url);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
        if (!url.hostname) return false;
        if (url.username || url.password) return false;
        return true;
      } catch {
        return false;
      }
    },
  },
});
</script>

<style module>

@value ( jcInformational900 ) from 'css/brand-colors.css';

.errorMessage {
  font-size: inherit !important;
  hyphens: auto;
  margin: 0;
  text-align: start !important;
  word-break: break-word;
}

.linksContainer {
  align-items: flex-start;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.2rem;
}

.helpLink {
  color: jcInformational900;
  cursor: pointer;
  text-decoration: none;
  text-wrap: nowrap;
}

.helpLink:hover {
  border-bottom: 1px solid jcInformational900;
  color: jcInformational900;
}

.externalLinkIcon {
  margin-left: 0.25em;
}

.helpLink .externalLinkIcon path {
  fill: jcInformational900;
}
</style>
